import { Component } from './component.js';
import { ThemeEvents } from './events.js';

/**
 * Sticky Add to Cart Component
 * 
 * Shows sticky ATC bar when original buy buttons are scrolled out of view
 */
export class StickyATCComponent extends Component {
  /**
   * @type {Element | null}
   */
  originalBuyButtons = null;

  /**
   * @type {IntersectionObserver | null}
   */
  intersectionObserver = null;

  /**
   * Latest subscription CTA markup mirrored from the main product form.
   * @type {string | null}
   */
  subscriptionCtaHtml = null;

  /**
   * Currently selected selling plan id (empty when one-time).
   * @type {string}
   */
  currentSellingPlan = '';

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  init() {
    // Find the original buy buttons in product-information section
    this.originalBuyButtons = document.querySelector('.product-information [ref="addToCartButton"]');
    
    if (!this.originalBuyButtons) {
      return;
    }

    this.setupIntersectionObserver();
    this.setupScrollHandler();
    this.setupEventListeners();
    this.setupResizeObserver();
    this.syncInitialCtaState();
    this.updateStickyVisibility();

    // Initial visibility check
    setTimeout(() => {
      this.syncInitialCtaState();
      this.updateStickyVisibility();
    }, 100);
  }

  setupResizeObserver() {
    const ro = new ResizeObserver(() => {
      this.updateStickyATCHeight();
    });
    ro.observe(this);
  }

  setupScrollHandler() {
    // Add scroll handler for more reliable detection
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.updateStickyVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  updateStickyVisibility() {
    if (!this.originalBuyButtons) return;
    const rect = this.originalBuyButtons.getBoundingClientRect();
    const header = document.querySelector('#header-component');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const isVisible = rect.top < window.innerHeight && rect.bottom > headerBottom;

    if (isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  setupIntersectionObserver() {
    if (!this.originalBuyButtons) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: [0, 0.1, 1]
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.updateStickyVisibility();
      });
    }, options);

    this.intersectionObserver.observe(this.originalBuyButtons);
  }

  setupEventListeners() {
    document.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate.bind(this));
    document.addEventListener('cart:item-added', this.handleCartSuccess.bind(this));
    document.addEventListener('subscription:cta-update', this.handleSubscriptionCtaUpdate.bind(this));
    // Intercept changes from variant pickers inside the sticky bar and delegate to the main picker
    this.addEventListener('change', this.handleStickyPickerChange.bind(this));
  }

  /**
   * Mirror the subscription/one-time CTA state from the main product form.
   * @param {CustomEvent} event
   */
  handleSubscriptionCtaUpdate(event) {
    const { html, sellingPlan } = event.detail || {};
    this.applyCtaState(html ?? null, sellingPlan ?? '');
  }

  /**
   * Read the current CTA/subscription state from the main product form and mirror it.
   * Used on load so the sticky bar reflects the default (e.g. subscription) selection.
   */
  syncInitialCtaState() {
    const mainButton = document.querySelector('.product-information [ref="addToCartButton"]');
    const mainContent = mainButton?.querySelector('.add-to-cart-text__content');
    if (!mainContent) return;

    const widget = document.querySelector('.product-information [ref="subscriptionWidget"]');
    const isSubscription = widget?.querySelector('input[data-purchase-type="subscription"]:checked') != null;
    let sellingPlan = '';
    if (isSubscription && widget) {
      const checkedPlan = widget.querySelector('input.subscription-widget__frequency-radio:checked');
      const firstPlan = widget.querySelector('input.subscription-widget__frequency-radio');
      sellingPlan =
        (checkedPlan instanceof HTMLInputElement ? checkedPlan.value : '') ||
        (firstPlan instanceof HTMLInputElement ? firstPlan.value : '') ||
        '';
    }

    this.applyCtaState(mainContent.innerHTML, sellingPlan);
  }

  /**
   * Apply a CTA label/price and selling plan to the sticky bar.
   * @param {string | null} html
   * @param {string} sellingPlan
   */
  applyCtaState(html, sellingPlan) {
    this.subscriptionCtaHtml = html ?? null;
    this.currentSellingPlan = sellingPlan ?? '';

    // Mirror the CTA label/price
    if (this.subscriptionCtaHtml != null) {
      this.querySelectorAll('.add-to-cart-button .add-to-cart-text__content').forEach((el) => {
        el.innerHTML = /** @type {string} */ (this.subscriptionCtaHtml);
      });
    }

    // Keep the sticky form(s) submitting with the correct selling plan
    this.querySelectorAll('form').forEach((form) => {
      let input = form.querySelector('input[name="selling_plan"]');
      if (this.currentSellingPlan) {
        if (!input) {
          input = document.createElement('input');
          input.setAttribute('type', 'hidden');
          input.setAttribute('name', 'selling_plan');
          form.appendChild(input);
        }
        if (input instanceof HTMLInputElement) {
          input.value = this.currentSellingPlan;
          input.disabled = false;
        }
      } else if (input instanceof HTMLInputElement) {
        input.value = '';
        input.disabled = true;
      }
    });
  }

  /**
   * Handle scroll to top for select options buttons
   */
  scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  /**
   * Handle change events from variant pickers inside the sticky ATC bar.
   * Instead of letting the picker fetch on its own (which would morph it with the wrong HTML),
   * we find the equivalent option in the main picker and trigger a native change there.
   * The main picker then fetches, fires variant:update, and handleVariantUpdate syncs back.
   * @param {Event} event
   */
  handleStickyPickerChange(event) {
    if (!event.target?.closest('variant-picker')) return;

    /** @type {string | undefined} */
    let optionValueId;

    if (event.target instanceof HTMLSelectElement) {
      const selectedOpt = event.target.options[event.target.selectedIndex];
      optionValueId = selectedOpt?.dataset.optionValueId;
    } else if (event.target instanceof HTMLInputElement) {
      optionValueId = event.target.dataset.optionValueId;
    }

    if (!optionValueId) return;

    this.syncToMainPicker(optionValueId);
  }

  /**
   * Trigger the equivalent variant change on the main product picker.
   * Dispatching a native change event causes VariantPicker.variantChanged to run,
   * which fetches updated HTML and fires variant:update — picked up by handleVariantUpdate.
   * @param {string} optionValueId
   */
  syncToMainPicker(optionValueId) {
    const mainPicker = document.querySelector('.product-information variant-picker');
    if (!mainPicker) return;

    // Buttons / swatch / pill style — radio input
    const radio = mainPicker.querySelector(`input[data-option-value-id="${optionValueId}"]`);
    if (radio instanceof HTMLInputElement) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Sibling products — button (not a real form input, so .click() it instead of dispatching 'change')
    const siblingButton = mainPicker.querySelector(`button[data-option-value-id="${optionValueId}"]`);
    if (siblingButton instanceof HTMLButtonElement && !siblingButton.disabled) {
      siblingButton.click();
      return;
    }

    // Dropdown style — option element
    const option = mainPicker.querySelector(`option[data-option-value-id="${optionValueId}"]`);
    if (option instanceof HTMLOptionElement) {
      const select = option.closest('select');
      if (select instanceof HTMLSelectElement) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * Handle variant:update events from the main product section.
   * Syncs the sticky ATC hidden input, variant picker visual state, price and button.
   * @param {CustomEvent} event
   */
  handleVariantUpdate(event) {
    // Only react to events originating from the main product information section
    if (!event.target?.closest('.product-information')) return;
    // Guard against events from inside this element (avoid feedback loops)
    if (this.contains(event.target)) return;

    const { resource } = event.detail;
    if (!resource) return;

    // 1. Update hidden variant ID inputs inside sticky forms
    this.querySelectorAll('input[name="id"]').forEach(input => {
      if (input instanceof HTMLInputElement) input.value = resource.id;
    });

    // 2. Sync variant picker visual selection state
    //    selectedOptionsValues is available after morph runs (before the event fires)
    const sourcePicker = event.target;
    if (sourcePicker instanceof Element && 'selectedOptionsValues' in sourcePicker) {
      const selectedValues = /** @type {string[]} */ (/** @type {any} */ (sourcePicker).selectedOptionsValues);
      this.querySelectorAll('variant-picker').forEach(picker => {
        this.syncPickerSelection(picker, selectedValues);
      });
    }

    // 3. Update price and button state
    this.updatePriceDisplay(resource);
    this.updateButtonState(resource);

    // 4. When switching to a different product entirely (combined listing / sibling switch),
    //    the title and thumbnail aren't part of `resource` (a variant), so pull them from the
    //    freshly fetched page instead.
    if (event.detail.data.newProduct && event.detail.data.html) {
      this.updateProductInfo(event.detail.data.html);
    }
  }

  /**
   * Sync the sticky bar's title and thumbnail image from a freshly fetched product page.
   * Supports both the monolithic `.sticky-atc__title`/`.sticky-atc__image` markup and the
   * generic theme-blocks version, where a "product-title" block renders as `[class*="__product_title"]`.
   * @param {Document} newHtml
   */
  updateProductInfo(newHtml) {
    const titleSelector = '.sticky-atc__title, [class*="__product_title"]';
    const newTitle = newHtml.querySelector(`sticky-atc-component ${titleSelector}`);
    if (newTitle) {
      this.querySelectorAll(titleSelector).forEach((title) => {
        title.textContent = newTitle.textContent;
      });
    }

    const imageSelector = '.sticky-atc__image img, [class*="__product_media"] img';
    const newImage = newHtml.querySelector(`sticky-atc-component ${imageSelector}`);
    if (newImage instanceof HTMLImageElement) {
      this.querySelectorAll(imageSelector).forEach((img) => {
        if (!(img instanceof HTMLImageElement)) return;
        img.src = newImage.src;
        img.srcset = newImage.srcset;
        img.alt = newImage.alt;
      });
    }
  }

  /**
   * Update a variant picker's visual selection without triggering its change handler.
   * Supports both radio/swatch (button style) and dropdown (select style).
   * Programmatic DOM property changes do not dispatch change events.
   * @param {Element} picker
   * @param {string[]} selectedOptionValueIds
   */
  syncPickerSelection(picker, selectedOptionValueIds) {
    selectedOptionValueIds.forEach(valueId => {
      // Button / swatch / pill style — radio inputs
      const radio = picker.querySelector(`input[data-option-value-id="${valueId}"]`);
      if (radio instanceof HTMLInputElement) {
        radio.checked = true;
        return;
      }

      // Dropdown style — select elements
      picker.querySelectorAll('select').forEach(select => {
        const matchingOption = Array.from(select.options).find(
          opt => opt.dataset.optionValueId === valueId
        );
        if (matchingOption) {
          Array.from(select.options).forEach(o => o.removeAttribute('selected'));
          matchingOption.setAttribute('selected', 'selected');
          select.value = matchingOption.value;
        }
      });
    });
  }

  /**
   * Update price display in sticky bar
   * @param {any} variant 
   */
  updatePriceDisplay(variant) {
    const priceContainers = this.querySelectorAll('[ref="priceContainer"], .sticky-atc__price');
    priceContainers.forEach(container => {
      const priceSpan = container.querySelector('.price');
      if (priceSpan && variant.price !== undefined) {
        // Format price using Shopify's money format
        const formattedPrice = this.formatMoney(variant.price);
        priceSpan.textContent = formattedPrice;
      }

      // Update compare at price if exists
      const compareAtPriceSpan = container.querySelector('.compare-at-price');
      if (compareAtPriceSpan && variant.compare_at_price && variant.compare_at_price > variant.price) {
        const formattedComparePrice = this.formatMoney(variant.compare_at_price);
        compareAtPriceSpan.textContent = formattedComparePrice;
        if (compareAtPriceSpan instanceof HTMLElement) {
          compareAtPriceSpan.style.display = 'inline';
        }
      } else if (compareAtPriceSpan instanceof HTMLElement) {
        compareAtPriceSpan.style.display = 'none';
      }
    });
  }

  /**
   * Update button state based on variant availability
   * @param {any} variant 
   */
  updateButtonState(variant) {
    const addToCartButtons = this.querySelectorAll('button[type="submit"], .sticky-atc__atc-button');
    addToCartButtons.forEach(button => {
      if (button instanceof HTMLButtonElement) {
        if (variant.available) {
          button.disabled = false;
          const textEl = button.querySelector('.add-to-cart-text__content');
          if (textEl) {
            if (this.subscriptionCtaHtml != null) {
              textEl.innerHTML = this.subscriptionCtaHtml;
            } else {
              const addText = button.dataset.addToCartText || button.dataset.addToCartLabel || 'Add to cart';
              textEl.innerHTML =
                button.dataset.showPrice === 'true'
                  ? `${addText}<span class="atc-price"> - ${this.formatMoney(variant.price)}</span>`
                  : addText;
            }
          }
        } else {
          button.disabled = true;
          const textEl = button.querySelector('.add-to-cart-text__content');
          if (textEl) textEl.textContent = button.dataset.soldOutText || 'Sold out';
        }
      }
    });
  }

  /**
   * Update product image when variant changes
   * @param {any} variant 
   */
  updateProductImage(variant) {
    const imageContainer = this.querySelector('.sticky-atc__image');
    if (!imageContainer) return;

    const img = imageContainer.querySelector('img');
    if (!img) return;

    if (variant.featured_media && variant.featured_media.preview_image) {
      const newSrc = variant.featured_media.preview_image.src.replace(/\.(jpg|jpeg|png|gif|webp).*/, '_80x80.$1');
      img.src = newSrc;
      img.alt = variant.featured_media.alt || '';
    }
  }

  /**
   * Handle successful cart addition
   */
  handleCartSuccess() {
    // Hide sticky bar temporarily after successful add to cart
    this.hide();
    
    // Show again after 2 seconds by re-checking visibility
    setTimeout(() => {
      this.updateStickyVisibility();
    }, 2000);
  }

  /**
   * Check if we are scrolled below the original ATC buttons
   * @returns {boolean}
   */
  isBelowOriginalATC() {
    if (!this.originalBuyButtons) return false;
    
    const rect = this.originalBuyButtons.getBoundingClientRect();
    // We are below the ATC if the top of the element is above the viewport
    return rect.bottom < 0;
  }

  /**
   * Check if original buy buttons are visible in viewport
   * @returns {boolean}
   */
  isOriginalVisible() {
    if (!this.originalBuyButtons) return false;
    
    const rect = this.originalBuyButtons.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    
    return rect.top >= 0 && rect.bottom <= windowHeight;
  }

  /**
   * Update sticky ATC height so it can be used for floating widgets
   */
  updateStickyATCHeight() {
    if (this.classList.contains('is-visible')) {
      document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
    }
  }

  /**
   * Show sticky ATC bar
   */
  show() {
    this.classList.add('is-visible');
    document.body.classList.add('sticky-atc-visible');
    this.updateStickyATCHeight();
  }

  /**
   * Hide sticky ATC bar
   */
  hide() {
    this.classList.remove('is-visible');
    document.body.classList.remove('sticky-atc-visible');
  }

  /**
   * Format money using Shopify's format or fallback
   * @param {number} cents 
   * @returns {string}
   */
  formatMoney(cents) {
    // @ts-ignore - Shopify global may not be typed
    if (typeof window.Shopify?.formatMoney === 'function') {
      // @ts-ignore
      return window.Shopify.formatMoney(cents);
    }
    
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }
}

// Register the custom element
if (customElements && !customElements.get('sticky-atc-component')) {
  customElements.define('sticky-atc-component', StickyATCComponent);
}